import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Controller, type Path, useForm } from "react-hook-form";
import {
  useReportVersionHeader,
  useUpdateReportVersionHeader,
} from "@/hooks/useReportQueries";
import { handleApiError } from "@/hooks/useApiQueries";
import {
  Button,
  ErrorDisplay,
  FormDatePicker,
  FormInput,
  Loader,
  NoData,
} from "@/shared/components";
import { useToast } from "@/shared/hooks/useToast";
import type {
  UiReportVersionHeader,
  UiValidationError,
} from "@/generated/ui-api";
import { useAuth } from "@/auth";
import FormPhoneInput from "@/shared/components/form/FormPhoneInput";

interface ReportVersionHeaderProps {
  versionId: number;
  onEditingChange?: (isEditing: boolean) => void;
  onSaveSuccess?: () => void;
}

const VALID_HEADER_FIELDS: string[] = [
  "vatPayerRegistrationInfo.vatPayerDocumentRegistrationNumber",
  "vatPayerRegistrationInfo.vatPayerDocumentRegistrationDate",
  "responsiblePersonInfo.preparationTime",
  "vatPayerInfo.vatPayerTitle",
  "vatPayerInfo.vatPayerIdentifierVAT",
  "vatPayerInfo.vatPayerAddress",
  "vatPayerInfo.vatPayerZipCode",
  "vatPayerInfo.vatPayerPhone",
  "vatPayerInfo.vatPayerEmail",
  "brokerInfo.brokerTitle",
  "brokerInfo.brokerIdentifierJAR",
  "brokerInfo.brokerAddress",
  "brokerInfo.brokerZipCode",
  "brokerInfo.brokerPhone",
  "brokerInfo.brokerEmail",
  "responsiblePersonInfo.responsiblePersonFirstLastName",
  "responsiblePersonInfo.responsiblePersonPhone",
  "responsiblePersonInfo.responsiblePersonEmail",
];

export default function ReportVersionHeader({
  versionId,
  onEditingChange,
  onSaveSuccess,
}: ReportVersionHeaderProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { representationMode } = useAuth();

  const {
    data: headerData,
    isLoading: loading,
    error,
    isError,
  } = useReportVersionHeader(versionId);

  const { mutate: updateHeader, isPending: isUpdating } =
    useUpdateReportVersionHeader(versionId);

  const { control, handleSubmit, reset, setError, clearErrors } =
    useForm<UiReportVersionHeader>();

  const handleValidationErrors = useCallback(
    (validationErrors: UiValidationError[]) => {
      clearErrors();
      validationErrors.forEach(validationError => {
        if (validationError.validationRuleDataFields) {
          validationError.validationRuleDataFields.forEach(field => {
            const fieldName = field.dataFieldName;

            if (fieldName && VALID_HEADER_FIELDS.includes(fieldName)) {
              setError(fieldName as Path<UiReportVersionHeader>, {
                type: "manual",
                message: validationError.messageText,
              });
            }
          });
        }
      });
    },
    [setError, clearErrors]
  );

  useEffect(() => {
    if (headerData) {
      reset(headerData);
      onEditingChange?.(!!headerData.isEditable);
      if (headerData.validationErrors) {
        handleValidationErrors(headerData.validationErrors);
      }
    }
  }, [headerData, reset, onEditingChange, handleValidationErrors]);

  const onSave = (data: UiReportVersionHeader) => {
    updateHeader(data, {
      onSuccess: () => {
        showToast(t("common.saved"), "success");
        onSaveSuccess?.();
      },
      onError: (error: unknown) => {
        const errObj = error as { body?: unknown };
        const validationErrors = errObj?.body || error;
        if (
          Array.isArray(validationErrors) &&
          validationErrors.length > 0 &&
          "validationRuleDataFields" in validationErrors[0]
        ) {
          handleValidationErrors(validationErrors as UiValidationError[]);
        } else {
          showToast(handleApiError(error as Error, t), "error");
        }
      },
    });
  };

  const renderField = useCallback(
    (
      label: string | undefined,
      value: string | undefined,
      fieldPath: Path<UiReportVersionHeader>,
      isPermanentlyReadOnly?: boolean,
      inputType:
        | "text"
        | "email"
        | "password"
        | "number"
        | "tel"
        | "url"
        | "time"
        | "date" = "text",
      isRequired: boolean = false
    ) => {
      const isFieldReadOnly = !headerData?.isEditable || isPermanentlyReadOnly;

      return (
        <Controller
          name={fieldPath}
          control={control}
          rules={
            isRequired && !isFieldReadOnly
              ? { required: t("error.fieldRequired") }
              : undefined
          }
          render={({ field, fieldState: { error } }) => {
            if (inputType === "date") {
              return (
                <FormDatePicker
                  label={label}
                  value={field.value as string}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  className="mt-1"
                  readOnly={isFieldReadOnly}
                  required={isRequired && !isFieldReadOnly}
                  error={error?.message}
                />
              );
            }

            if (inputType === "tel") {
              return (
                <FormPhoneInput
                  label={label}
                  value={field.value as string}
                  onChange={field.onChange}
                  className="mt-1"
                  readOnly={isFieldReadOnly}
                  required={isRequired && !isFieldReadOnly}
                  error={error?.message}
                />
              );
            }

            const displayValue =
              isFieldReadOnly && value !== undefined
                ? value
                : (field.value as string) || "";

            return (
              <FormInput
                label={label}
                value={displayValue}
                onChange={field.onChange}
                onBlur={field.onBlur}
                type={inputType}
                placeholder=""
                className="mt-1"
                readOnly={isFieldReadOnly}
                required={isRequired && !isFieldReadOnly}
                error={error?.message}
              />
            );
          }}
        />
      );
    },
    [headerData?.isEditable, control, t]
  );

  if (loading) {
    return <Loader />;
  }

  if (isError) {
    return <ErrorDisplay message={handleApiError(error, t)} />;
  }

  if (!headerData) {
    return <NoData />;
  }

  const hasBrokerValues = !!(
    headerData.brokerInfo?.brokerTitle ||
    headerData.brokerInfo?.brokerIdentifierJAR ||
    headerData.brokerInfo?.brokerAddress ||
    headerData.brokerInfo?.brokerZipCode ||
    headerData.brokerInfo?.brokerPhone ||
    headerData.brokerInfo?.brokerEmail
  );

  return (
    <div className="space-y-8 pt-4">
      {/* Vat Payer Registration Info */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-900">
          {t("reportVersionHeaderPage.vatPayerRegistrationInfo")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex flex-col justify-end">
            {renderField(
              t("reportVersionHeaderPage.registrationNumber"),
              headerData.vatPayerRegistrationInfo
                ?.vatPayerDocumentRegistrationNumber,
              "vatPayerRegistrationInfo.vatPayerDocumentRegistrationNumber"
            )}
          </div>
          <div className="flex flex-col justify-end">
            {renderField(
              t("reportVersionHeaderPage.registrationDate"),
              undefined,
              "vatPayerRegistrationInfo.vatPayerDocumentRegistrationDate",
              false,
              "date"
            )}
          </div>
          <Controller
            name="responsiblePersonInfo.preparationTime"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const val = (field.value as string) || "";
              const [hours = "", minutes = ""] = val.split(":");
              const isFieldReadOnly = !headerData?.isEditable;

              const displayValue =
                (isFieldReadOnly &&
                headerData?.responsiblePersonInfo?.preparationTime !== undefined
                  ? headerData.responsiblePersonInfo.preparationTime
                  : val) || "";
              const [displayHours = "", displayMinutes = ""] =
                displayValue.split(":");

              return (
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-black mb-2">
                    {t("reportVersionHeaderPage.preparationTime")}
                  </label>
                  <div className="flex gap-4">
                    <FormInput
                      label={t("common.hours")}
                      value={displayHours}
                      readOnly={isFieldReadOnly}
                      type="text"
                      className="flex-1 mt-1"
                      onChange={newHours => {
                        const sanitizedHours = newHours.replace(/[^0-9]/g, "");
                        if (!sanitizedHours && !minutes) {
                          field.onChange("");
                        } else {
                          field.onChange(`${sanitizedHours}:${minutes || "0"}`);
                        }
                      }}
                    />
                    <FormInput
                      label={t("common.minutes")}
                      value={displayMinutes}
                      readOnly={isFieldReadOnly}
                      type="text"
                      className="flex-1 mt-1"
                      onChange={newMinutes => {
                        let sanitizedMinutes = newMinutes.replace(
                          /[^0-9]/g,
                          ""
                        );
                        if (sanitizedMinutes !== "") {
                          if (parseInt(sanitizedMinutes) > 59) {
                            sanitizedMinutes = "59";
                          }
                        }
                        if (!hours && !sanitizedMinutes) {
                          field.onChange("");
                        } else {
                          field.onChange(`${hours || "0"}:${sanitizedMinutes}`);
                        }
                      }}
                    />
                  </div>
                  {error && (
                    <p className="mt-1 text-sm text-red-600">{error.message}</p>
                  )}
                </div>
              );
            }}
          />
        </div>
      </div>

      {/* Vat Payer Info */}
      {headerData.vatPayerInfo && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">
            {t("reportVersionHeaderPage.vatPayerInfo")}
          </h2>
          <div className="space-y-6">
            <div className="flex flex-col gap-6 max-w-lg">
              {renderField(
                t("reportVersionHeaderPage.title"),
                headerData.vatPayerInfo.vatPayerTitle,
                "vatPayerInfo.vatPayerTitle",
                false
              )}
              {renderField(
                t("reportVersionHeaderPage.vatNumber"),
                headerData.vatPayerInfo.vatPayerIdentifierVAT,
                "vatPayerInfo.vatPayerIdentifierVAT",
                true
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {renderField(
                t("reportVersionHeaderPage.address"),
                headerData.vatPayerInfo.vatPayerAddress,
                "vatPayerInfo.vatPayerAddress"
              )}
              {renderField(
                t("reportVersionHeaderPage.zipCode"),
                headerData.vatPayerInfo.vatPayerZipCode,
                "vatPayerInfo.vatPayerZipCode"
              )}
              {renderField(
                t("reportVersionHeaderPage.phone"),
                headerData.vatPayerInfo.vatPayerPhone,
                "vatPayerInfo.vatPayerPhone",
                false,
                "tel",
                true
              )}
              {renderField(
                t("reportVersionHeaderPage.email"),
                headerData.vatPayerInfo.vatPayerEmail,
                "vatPayerInfo.vatPayerEmail",
                false,
                "email",
                true
              )}
            </div>
          </div>
        </div>
      )}

      {/* Broker Info */}
      {(representationMode === "DELEGATED" || hasBrokerValues) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">
            {t("reportVersionHeaderPage.brokerInfo")}
          </h2>
          <div className="space-y-6">
            <div className="flex flex-col gap-6 max-w-lg">
              {renderField(
                t("reportVersionHeaderPage.brokerTitle"),
                headerData.brokerInfo?.brokerTitle,
                "brokerInfo.brokerTitle",
                false,
                "text",
                representationMode === "DELEGATED"
              )}
              {renderField(
                t("reportVersionHeaderPage.jarCode"),
                headerData.brokerInfo?.brokerIdentifierJAR,
                "brokerInfo.brokerIdentifierJAR",
                true,
                "text",
                representationMode === "DELEGATED"
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {renderField(
                t("reportVersionHeaderPage.address"),
                headerData.brokerInfo?.brokerAddress,
                "brokerInfo.brokerAddress"
              )}
              {renderField(
                t("reportVersionHeaderPage.zipCode"),
                headerData.brokerInfo?.brokerZipCode,
                "brokerInfo.brokerZipCode"
              )}
              {renderField(
                t("reportVersionHeaderPage.phone"),
                headerData.brokerInfo?.brokerPhone,
                "brokerInfo.brokerPhone",
                false,
                "tel",
                representationMode === "DELEGATED"
              )}
              {renderField(
                t("reportVersionHeaderPage.email"),
                headerData.brokerInfo?.brokerEmail,
                "brokerInfo.brokerEmail",
                false,
                "email",
                representationMode === "DELEGATED"
              )}
            </div>
          </div>
        </div>
      )}

      {/* Responsible Person Info */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-900">
          {t("reportVersionHeaderPage.responsiblePersonInfo")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {renderField(
            t("reportVersionHeaderPage.name"),
            headerData.responsiblePersonInfo?.responsiblePersonFirstLastName,
            "responsiblePersonInfo.responsiblePersonFirstLastName",
            false,
            "text",
            true
          )}
          {renderField(
            t("reportVersionHeaderPage.phone"),
            headerData.responsiblePersonInfo?.responsiblePersonPhone,
            "responsiblePersonInfo.responsiblePersonPhone",
            false,
            "tel",
            true
          )}
          {renderField(
            t("reportVersionHeaderPage.email"),
            headerData.responsiblePersonInfo?.responsiblePersonEmail,
            "responsiblePersonInfo.responsiblePersonEmail",
            false,
            "email",
            true
          )}
        </div>
      </div>

      <div className="flex gap-2 justify-start mt-6">
        {headerData.isEditable && (
          <Button
            variant="primary"
            onClick={handleSubmit(onSave)}
            loading={isUpdating}
          >
            {t("common.save")}
          </Button>
        )}
      </div>
    </div>
  );
}
